'use client'
/**
 * Sorveglia gli **eventi** di una sessione live per una sola cosa: le
 * sostituzioni.
 *
 * Serve perché la sostituzione, per scelta del reducer, non cambia lo stato
 * (`case 'substitution': // nessun cambiamento di stato`). Chi guarda solo
 * `useLiveSession` riceve il punteggio aggiornato ma non sa che è appena
 * entrato un cambio: senza gli eventi, il tabellone in onda non può mettere in
 * scena la sostituzione.
 *
 * Il canale è lo stesso della cronaca: `GET /api/live/<key>/events?after=<id>`,
 * una richiesta piccola che chiede solo ciò che è arrivato dopo l'ultimo id
 * visto. Al montaggio si legge l'ultimo id **senza animare**: un tabellone che
 * si apre a metà partita non deve mettere in onda le sostituzioni di un'ora fa.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { SUBSTITUTION_TOTAL_MS, type SubstitutionSwap } from '@/lib/live/substitution'
import { BLOCK_SCENE_MS, isMonsterBlockDetail, type BlockScene } from '@/hooks/useBlockScene'
import { ACE_SCENE_MS, type AceScene } from '@/hooks/useAceScene'
import type { LiveSide } from '@/lib/live/state'

/** Ogni quanto si chiede se è arrivato qualcosa. */
const POLL_MS = 1500

/** Quanti eventi chiedere per volta: bastano, la sostituzione è rara. */
const PAGE = 20

export interface LiveSubstitutionEvent extends SubstitutionSwap {
  /** id dell'evento: identifica un cambio e impedisce di animarlo due volte. */
  id: number
}

interface EventRow {
  id: number
  type: string
  team?: string | null
  payload?: {
    type?: string
    team?: string
    detail?: string
    /** Il tipo d'azione vero, quando `type` è una mappatura (`SET_COMMENTARY` → `note`). */
    originalType?: string
    visible?: boolean
  } | null
}

/**
 * Il commento va in onda **solo dopo** che lo scorer ha attribuito il punto:
 * l'operatore lo scrive e lo accende, ma la fascia compare quando l'azione
 * `point_scorer` arriva dopo l'accensione. Se il punto è già stato attribuito
 * (commento acceso dopo), la fascia non aspetta un altro punto.
 */
function commentaryGateOpen(commentaryOnId: number, pointScorerId: number): boolean {
  // Senza alcun punto attribuito il commento non va in onda.
  if (pointScorerId === 0) return false
  // Nessuna accensione manuale (il testo arriva dal generatore, che scrive dopo il
  // punto): basta che un punto ci sia stato.
  if (commentaryOnId === 0) return true
  // Acceso a mano: aspetta il punto successivo all'accensione.
  return pointScorerId > commentaryOnId
}

/** `«7→12»` → `{ out: '7', in: '12' }`; qualunque altra forma → null. */
export function parseSubstitutionDetail(detail: string | undefined | null): { out: string | null; in: string | null } {
  if (!detail) return { out: null, in: null }
  const parts = detail.split(/→|->/)
  if (parts.length !== 2) return { out: null, in: null }
  const clean = (v: string) => v.trim() || null
  return { out: clean(parts[0]), in: clean(parts[1]) }
}

function isSide(value: unknown): value is LiveSide {
  return value === 'home' || value === 'away'
}

export function useLiveSubstitution(sessionKey: string, enabled = true) {
  const [substitution, setSubstitution] = useState<LiveSubstitutionEvent | null>(null)
  /**
   * Le due scene del tabellone partono **da questo stesso flusso**: chi guarda
   * l'anteprima della console vede gli stessi mattoni e lo stesso pallone che
   * vanno in onda sul tabellone pubblico, con gli stessi id d'evento.
   */
  const [block, setBlock] = useState<BlockScene | null>(null)
  const [ace, setAce] = useState<AceScene | null>(null)
  const blockTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const aceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [commentaryGate, setCommentaryGate] = useState(false)
  /**
   * La modalità si spegne da sola dopo i tre cicli: chi disegna il tabellone non
   * deve tenere un timer, e due viste aperte (console e pannello) restano
   * d'accordo sulla stessa durata.
   */
  const autoOffRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastIdRef = useRef<number | null>(null)
  const seenRef = useRef<number>(0)
  const commentaryOnRef = useRef(0)
  const pointScorerRef = useRef(0)
  /**
   * Poll immediato per chi **ha appena scritto** il cambio: la console non deve
   * aspettare il giro dei 1,5 s per mettere in scena la sostituzione appena
   * inviata.
   */
  const pollRef = useRef<(() => void) | null>(null)

  const clear = useCallback(() => setSubstitution(null), [])
  const refresh = useCallback(() => { pollRef.current?.() }, [])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const request = async (after: number | null, limit = 1): Promise<EventRow[]> => {
      const query = after === null ? `limit=${limit}` : `after=${after}&limit=${PAGE}`
      try {
        const r = await fetch(`/api/live/${sessionKey}/events?${query}`, { cache: 'no-store' })
        if (!r.ok) return []
        const j = (await r.json()) as { events?: EventRow[] }
        return Array.isArray(j.events) ? j.events : []
      } catch {
        return []
      }
    }

    /**
     * Il primo giro non anima: prende il segnaposto dell'ultimo evento e
     * ricostruisce, sugli ultimi eventi, se il commento e gia «dopo il punto».
     */
    const baseline = async (): Promise<void> => {
      const rows = await request(null, 50)
      if (cancelled) return
      // Senza `after` la query è DESC: il primo è l'ultimo evento.
      lastIdRef.current = rows[0]?.id ?? 0
      seenRef.current = lastIdRef.current
      for (const row of rows) {
        if (row.type === 'point_scorer') pointScorerRef.current = Math.max(pointScorerRef.current, row.id)
        if (row.type === 'note' && row.payload?.originalType === 'SET_COMMENTARY' && row.payload?.visible === true) {
          commentaryOnRef.current = Math.max(commentaryOnRef.current, row.id)
        }
      }
      setCommentaryGate(commentaryGateOpen(commentaryOnRef.current, pointScorerRef.current))
    }

    const poll = async (): Promise<void> => {
      const after = lastIdRef.current
      if (after === null) return
      const rows = await request(after)
      if (cancelled) return
      for (const row of rows) {
        if (row.id > (lastIdRef.current ?? 0)) lastIdRef.current = row.id
        if (row.type === 'point_scorer') pointScorerRef.current = Math.max(pointScorerRef.current, row.id)
        if (
          row.type === 'note' &&
          row.payload?.originalType === 'SET_COMMENTARY' &&
          row.payload?.visible === true
        ) {
          commentaryOnRef.current = Math.max(commentaryOnRef.current, row.id)
        }
        setCommentaryGate(commentaryGateOpen(commentaryOnRef.current, pointScorerRef.current))
        if (row.id <= seenRef.current) continue
        seenRef.current = row.id
        const team = isSide(row.team) ? row.team : isSide(row.payload?.team) ? row.payload.team : null
        if (!team) continue
        // ── Ace: la palla batte nel campo di chi lo subisce ──────────────────
        if (row.type === 'ace') {
          setAce({ team, id: row.id })
          if (aceTimer.current) clearTimeout(aceTimer.current)
          aceTimer.current = setTimeout(() => setAce(null), ACE_SCENE_MS)
          continue
        }
        // ── Monster block: solo il muro che fa punto ────────────────────────
        if (row.type === 'block' && isMonsterBlockDetail(row.payload?.detail)) {
          setBlock({ team, id: row.id })
          if (blockTimer.current) clearTimeout(blockTimer.current)
          blockTimer.current = setTimeout(() => setBlock(null), BLOCK_SCENE_MS)
          continue
        }
        if (row.type !== 'substitution') continue
        const { out, in: incoming } = parseSubstitutionDetail(row.payload?.detail)
        setSubstitution({ id: row.id, team, out, in: incoming })
        if (autoOffRef.current) clearTimeout(autoOffRef.current)
        autoOffRef.current = setTimeout(() => setSubstitution(null), SUBSTITUTION_TOTAL_MS)
      }
    }

    pollRef.current = () => { void poll() }

    const tick = async (): Promise<void> => {
      if (cancelled) return
      if (document.visibilityState === 'hidden') {
        timer = setTimeout(() => void tick(), POLL_MS)
        return
      }
      await poll()
      if (cancelled) return
      timer = setTimeout(() => void tick(), POLL_MS)
    }

    void (async () => {
      await baseline()
      if (cancelled) return
      timer = setTimeout(() => void tick(), POLL_MS)
    })()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      if (autoOffRef.current) {
        clearTimeout(autoOffRef.current)
        autoOffRef.current = null
      }
      if (blockTimer.current) {
        clearTimeout(blockTimer.current)
        blockTimer.current = null
      }
      if (aceTimer.current) {
        clearTimeout(aceTimer.current)
        aceTimer.current = null
      }
    }
  }, [sessionKey, enabled])

  return { substitution, block, ace, commentaryGate, clear, refresh }
}
